import { useState, useMemo } from 'react';
import {
    Box,
    Chip,
    CircularProgress,
    Stack,
    Table,
    TableBody,
    TableContainer,
    TablePagination,
    Alert
} from '@mui/material';
import { TableCard } from '@/components/shared/Card';
import CardHeader from '@/components/shared/CardHeader';
import { SearchGrid } from '@/components/SearchGrid';
import { DateRangeDropdown } from '@/components/shared/DateRangeDropdown';
import {
    TableCell,
    TableFooter,
    TableFooterCell,
    TableHeader,
    TableHeaderCell,
    TableRow
} from '@/components/shared/styles/Table';
import { TextMDSemiBold, SMRegular, SMBold } from '@/typography/Typography';
import {
    DangerButton,
    OutlinedSecondaryButton,
    PrimaryButton
} from '@/components/shared/Button';
import { InputText } from '@/components/shared/InputText';
import { Dropdown } from '@/components/shared/Dropdown';
import { useExecuteReport } from '@/services/reportAIQuery';
import {
    ReportDefinitionResponse,
    ReportColumnSchema,
    ReportChartConfig
} from '@/models/reportAI';
import dayjs from 'dayjs';
import { DATE_FORMAT } from '@/lib/constants';
import {
    useReactTable,
    getCoreRowModel,
    getPaginationRowModel,
    flexRender,
    ColumnDef
} from '@tanstack/react-table';
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    Tooltip,
    ResponsiveContainer,
    LineChart,
    Line,
    PieChart,
    Pie,
    Cell,
    Legend,
    CartesianGrid
} from 'recharts';

interface JSONSchemaProperty {
    type?: string;
    format?: string;
    title?: string;
    enum?: string[];
}

interface JSONSchema {
    properties?: Record<string, JSONSchemaProperty>;
    required?: string[];
}

interface DynamicReportViewerProps {
    definition: ReportDefinitionResponse;
    companyId: string;
    isSuperAdmin?: boolean;
    onDeactivate?: () => void;
    onToggleMode?: () => void;
}

const EMPTY_COLUMN: ColumnDef<Record<string, unknown>>[] = [
    { id: '_empty', accessorKey: '_empty', header: '_empty' }
];

const CHART_COLORS = [
    '#3B82F6',
    '#10B981',
    '#F59E0B',
    '#EF4444',
    '#8B5CF6',
    '#EC4899',
    '#14B8A6',
    '#F97316',
    '#6366F1',
    '#84CC16',
    '#06B6D4',
    '#E11D48'
];

const formatTooltipValue = (value: number) => value.toLocaleString('da-DK');

const MAX_PIE_SLICES = 8;

/** Group small pie slices into an "Others" bucket so the chart stays readable. */
const aggregatePieData = (
    rows: Record<string, unknown>[],
    nameKey: string,
    valueKey: string
): Record<string, unknown>[] => {
    if (rows.length <= MAX_PIE_SLICES) return rows;
    const sorted = [...rows].sort(
        (a, b) => (Number(b[valueKey]) || 0) - (Number(a[valueKey]) || 0)
    );
    const top = sorted.slice(0, MAX_PIE_SLICES - 1);
    const rest = sorted.slice(MAX_PIE_SLICES - 1);
    const othersValue = rest.reduce(
        (sum, r) => sum + (Number(r[valueKey]) || 0),
        0
    );
    return [
        ...top,
        { [nameKey]: `Others (${rest.length})`, [valueKey]: othersValue }
    ];
};

/** Returns dynamic chart height based on chart type and number of rows. */
const getChartHeight = (type: string, rowCount: number): number => {
    if (type === 'pie') return Math.min(420, Math.max(320, rowCount * 10));
    if (type === 'bar') return Math.min(500, Math.max(300, rowCount * 28));
    return 320; // line charts
};

const formatCellValue = (
    value: unknown,
    schema: Pick<ReportColumnSchema, 'type'> & { currencyKey?: string },
    row?: Record<string, unknown>
): string => {
    if (value === null || value === undefined) return '—';
    switch (schema.type) {
        case 'currency': {
            const resolvedKey =
                schema.currencyKey && row
                    ? Object.keys(row).find(
                          (k) =>
                              k.toLowerCase() ===
                              schema.currencyKey!.toLowerCase()
                      )
                    : undefined;
            const code =
                resolvedKey && row ? String(row[resolvedKey] ?? 'DKK') : 'DKK';
            return typeof value === 'number'
                ? new Intl.NumberFormat('da-DK', {
                      style: 'currency',
                      currency: code
                  }).format(value)
                : String(value);
        }
        case 'date':
            return dayjs(String(value)).format(DATE_FORMAT.DAY_MONTH_YEAR);
        case 'datetime':
            return dayjs(String(value)).format(DATE_FORMAT.FULL_DATE_TIME);
        case 'boolean':
            return value ? 'Yes' : 'No';
        default:
            return String(value);
    }
};

/**
 * Formats a column total for the table footer. Currency columns deliberately
 * print a bare locale number: a totals row can span rows with different
 * currency codes, so attaching one symbol would misstate the sum.
 */
const formatFooterTotal = (
    total: number,
    schema:
        | (Pick<ReportColumnSchema, 'type'> & { currencyKey?: string })
        | undefined,
    row?: Record<string, unknown>
): string =>
    schema?.type === 'currency'
        ? new Intl.NumberFormat('da-DK').format(total)
        : formatCellValue(total, schema ?? { type: 'number' }, row);

export const DynamicReportViewer = ({
    definition,
    companyId,
    isSuperAdmin,
    onDeactivate,
    onToggleMode
}: DynamicReportViewerProps) => {
    const [confirmDeactivate, setConfirmDeactivate] = useState(false);
    const columnSchema: ReportColumnSchema[] = useMemo(() => {
        try {
            return JSON.parse(definition.columnSchema);
        } catch {
            return [];
        }
    }, [definition.columnSchema]);

    const columnSchemaMap = useMemo(
        () =>
            Object.fromEntries(
                columnSchema
                    .filter((c) => c?.key != null)
                    .map((c) => [c.key.toLowerCase(), c])
            ),
        [columnSchema]
    );

    const parameterJsonSchema: JSONSchema = useMemo(() => {
        try {
            return JSON.parse(definition.parameterSchema);
        } catch {
            return { properties: {} };
        }
    }, [definition.parameterSchema]);

    const chartConfig: ReportChartConfig | null = useMemo(() => {
        if (!definition.chartConfig) return null;
        try {
            return JSON.parse(definition.chartConfig);
        } catch {
            return null;
        }
    }, [definition.chartConfig]);

    const [startDate, setStartDate] = useState<Date>(
        dayjs().startOf('month').toDate()
    );
    const [endDate, setEndDate] = useState<Date | null>(
        dayjs().endOf('month').toDate()
    );
    const [timeFilter, setTimeFilter] = useState('currentMonth');
    const [extraParams, setExtraParams] = useState<
        Record<string, string | undefined>
    >({});

    // Schema properties that are date fields — wired to DateRangeDropdown
    const dateKeys = useMemo(
        () =>
            Object.entries(parameterJsonSchema.properties ?? {})
                .filter(
                    ([, p]) => p.format === 'date' || p.format === 'date-time'
                )
                .map(([k]) => k),
        [parameterJsonSchema]
    );

    // Non-date params rendered individually in the SearchGrid
    const extraParamEntries = useMemo(
        () =>
            Object.entries(parameterJsonSchema.properties ?? {}).filter(
                ([, p]) => p.format !== 'date' && p.format !== 'date-time'
            ),
        [parameterJsonSchema]
    );

    // Reactively build params from date range + extra params — re-runs query on any change
    const params = useMemo<Record<string, string | undefined>>(() => {
        const built: Record<string, string | undefined> = { ...extraParams };
        if (dateKeys[0])
            built[dateKeys[0]] = dayjs(startDate).format('YYYY-MM-DD');
        if (dateKeys[1] && endDate)
            built[dateKeys[1]] = dayjs(endDate).format('YYYY-MM-DD');
        return built;
    }, [startDate, endDate, extraParams, dateKeys]);

    const { data, isFetching, isError, error } = useExecuteReport(
        definition.reportId,
        companyId,
        params,
        true
    );

    const filterOptions = [
        { value: 'currentMonth', name: 'Current Month' },
        { value: 'lastMonth', name: 'Last Month' },
        { value: 'currentYear', name: 'Current Year' },
        { value: 'lastYear', name: 'Last Year' },
        { value: 'customDate', name: 'Custom Date Range' }
    ];

    const onDateRangeChange = (dates: [Date | null, Date | null]) => {
        const [start, end] = dates;
        if (start) setStartDate(start);
        // Always update end (including null) so react-datepicker knows the
        // range is in-progress and can accept the second click correctly.
        setEndDate(end);
    };

    const handleApply = () => {
        setTimeFilter('customDate');
    };

    const filtereDateData = (option: string) => {
        if (option === 'currentMonth') {
            setStartDate(dayjs().startOf('month').toDate());
            setEndDate(dayjs().endOf('month').toDate());
        } else if (option === 'lastMonth') {
            setStartDate(
                dayjs().subtract(1, 'month').startOf('month').toDate()
            );
            setEndDate(dayjs().subtract(1, 'month').endOf('month').toDate());
        } else if (option === 'currentYear') {
            setStartDate(dayjs().startOf('year').toDate());
            setEndDate(dayjs().endOf('year').toDate());
        } else if (option === 'lastYear') {
            setStartDate(dayjs().subtract(1, 'year').startOf('year').toDate());
            setEndDate(dayjs().subtract(1, 'year').endOf('year').toDate());
        }
    };

    const onMonthChange = (date: Date) => {
        setTimeFilter('customMonth');
        setStartDate(dayjs(date).startOf('month').toDate());
        setEndDate(dayjs(date).endOf('month').toDate());
    };

    // @tanstack/react-table column definitions.
    // Derived from the actual row keys (camelCase) so accessorKey always matches
    // the JSON response. ColumnSchema is matched case-insensitively for labels/types.
    const tableColumns = useMemo<ColumnDef<Record<string, unknown>>[]>(() => {
        if (!data?.rows?.length) return [];

        const rowKeys = Object.keys(data.rows[0]);
        return rowKeys
            .filter((rowKey) => {
                const schemaEntry = columnSchema.find(
                    (c) =>
                        c?.key != null &&
                        c.key.toLowerCase() === rowKey.toLowerCase()
                );
                return !schemaEntry?.hidden;
            })
            .map((rowKey) => {
                const schemaEntry = columnSchema.find(
                    (c) =>
                        c?.key != null &&
                        c.key.toLowerCase() === rowKey.toLowerCase()
                );
                const header: string = schemaEntry?.label ?? rowKey;
                const colDef: ColumnDef<Record<string, unknown>> = {
                    id: rowKey,
                    accessorKey: rowKey,
                    header
                };
                if (schemaEntry) {
                    colDef.cell = (info) =>
                        formatCellValue(
                            info.getValue(),
                            schemaEntry,
                            info.row.original
                        );
                }
                return colDef;
            });
    }, [columnSchema, data?.rows]);

    const hasRows = (data?.rows?.length ?? 0) > 0 && tableColumns.length > 0;

    // Footer cell per numeric column, across all rows (not just the current page). Each column
    // declares how it aggregates: 'sum' for a flow, 'last' for a running balance, 'none' to
    // stay blank. A column that omits the directive sums, which is the right default for a flow.
    const columnTotals = useMemo<Record<string, number> | null>(() => {
        if (!data?.rows?.length || !columnSchema.length) return null;

        const rows = data.rows;
        const resolveRowKey = (key: string) =>
            Object.keys(rows[0]).find(
                (k) => k.toLowerCase() === key.toLowerCase()
            );

        const footer: Record<string, number> = {};
        for (const col of columnSchema) {
            if (col.hidden) continue;
            if (col.type !== 'number' && col.type !== 'currency') continue;

            const aggregate = col.footerAggregate ?? 'sum';
            if (aggregate === 'none') continue;

            const rowKey = resolveRowKey(col.key);
            if (rowKey === undefined) continue;

            footer[rowKey] =
                aggregate === 'last'
                    ? Number(rows[rows.length - 1][rowKey]) || 0
                    : rows.reduce(
                          (sum, row) => sum + (Number(row[rowKey]) || 0),
                          0
                      );
        }
        return Object.keys(footer).length ? footer : null;
    }, [data?.rows, columnSchema]);

    // Index of the first non-numeric column — where the "Total" label is placed
    const firstLabelColIdx = useMemo<number>(() => {
        if (!columnTotals) return 0;
        const rowKeys = data?.rows?.length ? Object.keys(data.rows[0]) : [];
        const idx = rowKeys.findIndex((k) => !(k in columnTotals));
        return idx >= 0 ? idx : 0;
    }, [columnTotals, data?.rows]);

    // Resolve PascalCase chart axis names to the actual camelCase row keys
    const { chartXKey, chartYKeys } = useMemo(() => {
        const rowKeys = data?.rows?.length ? Object.keys(data.rows[0]) : [];
        const resolve = (axisKey: string | undefined): string => {
            if (!axisKey) return '';
            return (
                rowKeys.find(
                    (k) => k.toLowerCase() === axisKey.toLowerCase()
                ) ?? axisKey
            );
        };
        // Support multiple data keys via dataKeys array, falling back to single yAxis
        const resolvedDataKeys: string[] = chartConfig?.dataKeys?.length
            ? chartConfig.dataKeys.map((dk) => resolve(dk))
            : chartConfig?.yAxis
              ? [resolve(chartConfig.yAxis)]
              : [];
        return {
            chartXKey: resolve(chartConfig?.xAxis),
            chartYKeys: resolvedDataKeys
        };
    }, [data?.rows, chartConfig]);

    // Whether X-axis labels need rotation (long text or many data points)
    const xAxisAngle = useMemo(() => {
        if (!data?.rows?.length || !chartXKey) return 0;
        const maxLen = Math.max(
            ...data.rows.map((r) => String(r[chartXKey] ?? '').length)
        );
        if (data.rows.length > 8 || maxLen > 12) return -35;
        if (data.rows.length > 5 || maxLen > 8) return -20;
        return 0;
    }, [data?.rows, chartXKey]);

    const pieData = useMemo(
        () =>
            chartConfig?.type === 'pie' && data?.rows
                ? aggregatePieData(data.rows, chartXKey, chartYKeys[0] ?? '')
                : (data?.rows ?? []),
        [chartConfig?.type, data?.rows, chartXKey, chartYKeys]
    );

    // TanStack Table returns freshly-created functions on every call, which
    // React Compiler cannot memoize without risking stale rows. It therefore
    // skips optimizing this component. Nothing to fix on our side — the only
    // alternative is dropping the library.
    // eslint-disable-next-line react-hooks/incompatible-library
    const table = useReactTable({
        data: data?.rows ?? [],
        columns: tableColumns.length > 0 ? tableColumns : EMPTY_COLUMN,
        getCoreRowModel: getCoreRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        initialState: { pagination: { pageSize: 50 } }
    });

    const hasFilters = dateKeys.length > 0 || extraParamEntries.length > 0;

    return (
        <Box>
            {isSuperAdmin && onDeactivate && (
                <Stack
                    direction="row"
                    justifyContent="flex-end"
                    alignItems="center"
                    gap={1}
                    mt={1}
                    mb={1}
                    pr={1}
                >
                    {confirmDeactivate ? (
                        <>
                            <SMRegular color="text.secondary">
                                Deactivate this report?
                            </SMRegular>
                            <DangerButton
                                size="small"
                                onClick={() => {
                                    setConfirmDeactivate(false);
                                    onDeactivate();
                                }}
                            >
                                Confirm
                            </DangerButton>
                            <PrimaryButton
                                size="small"
                                onClick={() => setConfirmDeactivate(false)}
                            >
                                Cancel
                            </PrimaryButton>
                        </>
                    ) : (
                        <>
                            {onToggleMode && (
                                <OutlinedSecondaryButton
                                    size="small"
                                    onClick={onToggleMode}
                                >
                                    {definition.isEnabled
                                        ? 'Put in Draft'
                                        : 'Enable'}
                                </OutlinedSecondaryButton>
                            )}
                            <OutlinedSecondaryButton
                                size="small"
                                onClick={() => setConfirmDeactivate(true)}
                            >
                                Deactivate
                            </OutlinedSecondaryButton>
                        </>
                    )}
                </Stack>
            )}
            <SearchGrid
                container
                sx={{ display: hasFilters ? undefined : 'none' }}
            >
                <Stack
                    direction="row"
                    alignItems="center"
                    gap={1.5}
                    flexWrap="wrap"
                    width="100%"
                    justifyContent="space-between"
                >
                    <Stack
                        direction="row"
                        alignItems="center"
                        gap={1}
                        flexWrap="wrap"
                    >
                        <SMBold noWrap>Filter by:</SMBold>
                        {dateKeys.length > 0 && (
                            <DateRangeDropdown
                                startDate={startDate}
                                endDate={endDate ?? undefined}
                                timeFilter={timeFilter}
                                setTimeFilter={setTimeFilter}
                                filterOptions={filterOptions}
                                filtereDateData={filtereDateData}
                                onMonthChange={onMonthChange}
                                showDatePicker={false}
                                onChange={onDateRangeChange}
                                handleApply={handleApply}
                            />
                        )}
                        {extraParamEntries.map(([key, prop]) =>
                            prop.enum && prop.enum.length > 0 ? (
                                <div key={key} style={{ minWidth: '180px' }}>
                                    <Dropdown
                                        allowClear
                                        label={prop.title ?? key}
                                        choices={prop.enum.map((v) => ({
                                            label: v,
                                            value: v
                                        }))}
                                        value={extraParams[key] ?? null}
                                        onChange={(v: string) =>
                                            setExtraParams((prev) => ({
                                                ...prev,
                                                [key]: v
                                            }))
                                        }
                                    />
                                </div>
                            ) : (
                                <div key={key} style={{ minWidth: '160px' }}>
                                    <InputText
                                        placeholder={prop.title ?? key}
                                        size="small"
                                        inputStyles={{
                                            backgroundColor: '#fff'
                                        }}
                                        type={
                                            prop.type === 'number' ||
                                            prop.type === 'integer'
                                                ? 'number'
                                                : 'text'
                                        }
                                        value={extraParams[key] ?? ''}
                                        onChange={(e) =>
                                            setExtraParams((prev) => ({
                                                ...prev,
                                                [key]: e.target.value
                                            }))
                                        }
                                    />
                                </div>
                            )
                        )}
                    </Stack>
                </Stack>
            </SearchGrid>
            <TableCard>
                <CardHeader
                    label={definition.name}
                    chip={
                        data && hasRows && !isFetching ? (
                            <Chip
                                label={
                                    <SMBold>
                                        {data.totalRows.toLocaleString()} rows
                                    </SMBold>
                                }
                                color="success"
                                size="small"
                            />
                        ) : undefined
                    }
                />

                {/* Error */}
                {isError && !isFetching && (
                    <Box px={2} pb={2}>
                        <Alert severity="error">
                            {(error as Error)?.message ??
                                'Failed to execute report.'}
                        </Alert>
                    </Box>
                )}

                {/* Loading */}
                {isFetching && (
                    <Box display="flex" justifyContent="center" py={6}>
                        <CircularProgress size={32} />
                    </Box>
                )}

                {/* Results */}
                {data && hasRows && !isFetching && (
                    <>
                        <TableContainer>
                            <Table size="small">
                                <TableHeader>
                                    {table.getHeaderGroups().map((hg) => (
                                        <TableRow key={hg.id}>
                                            {hg.headers.map((header) => {
                                                const col =
                                                    columnSchemaMap[
                                                        header.id.toLowerCase()
                                                    ];
                                                return (
                                                    <TableHeaderCell
                                                        key={header.id}
                                                        align={
                                                            col?.align ?? 'left'
                                                        }
                                                    >
                                                        {flexRender(
                                                            header.column
                                                                .columnDef
                                                                .header,
                                                            header.getContext()
                                                        )}
                                                    </TableHeaderCell>
                                                );
                                            })}
                                        </TableRow>
                                    ))}
                                </TableHeader>
                                <TableBody>
                                    {table.getRowModel().rows.map((row) => (
                                        <TableRow key={row.id}>
                                            {row
                                                .getVisibleCells()
                                                .map((cell) => {
                                                    const col =
                                                        columnSchemaMap[
                                                            cell.column.id.toLowerCase()
                                                        ];
                                                    return (
                                                        <TableCell
                                                            key={cell.id}
                                                            align={
                                                                col?.align ??
                                                                'left'
                                                            }
                                                        >
                                                            {flexRender(
                                                                cell.column
                                                                    .columnDef
                                                                    .cell,
                                                                cell.getContext()
                                                            )}
                                                        </TableCell>
                                                    );
                                                })}
                                        </TableRow>
                                    ))}
                                </TableBody>
                                {columnTotals && (
                                    <TableFooter>
                                        <TableRow>
                                            {table
                                                .getHeaderGroups()[0]
                                                ?.headers.map((header, idx) => {
                                                    const col =
                                                        columnSchemaMap[
                                                            header.id.toLowerCase()
                                                        ];
                                                    const total =
                                                        columnTotals[header.id];
                                                    return (
                                                        <TableFooterCell
                                                            key={header.id}
                                                            align={
                                                                col?.align ??
                                                                'left'
                                                            }
                                                        >
                                                            <SMBold>
                                                                {total !==
                                                                undefined
                                                                    ? formatFooterTotal(
                                                                          total,
                                                                          col,
                                                                          data
                                                                              ?.rows?.[0]
                                                                      )
                                                                    : idx ===
                                                                        firstLabelColIdx
                                                                      ? 'Total'
                                                                      : ''}
                                                            </SMBold>
                                                        </TableFooterCell>
                                                    );
                                                })}
                                        </TableRow>
                                    </TableFooter>
                                )}
                            </Table>
                        </TableContainer>
                        {data.totalRows > 50 && (
                            <TablePagination
                                component="div"
                                count={data.totalRows}
                                page={table.getState().pagination.pageIndex}
                                rowsPerPage={
                                    table.getState().pagination.pageSize
                                }
                                rowsPerPageOptions={[50]}
                                onPageChange={(_, newPage) =>
                                    table.setPageIndex(newPage)
                                }
                            />
                        )}

                        {/* Chart */}
                        {chartConfig &&
                            chartConfig.type !== 'text' &&
                            chartYKeys.length > 0 && (
                                <Box px={2} pt={3} pb={2}>
                                    <TextMDSemiBold sx={{ mb: 1 }}>
                                        {chartConfig.title ?? 'Chart'}
                                    </TextMDSemiBold>
                                    <Box
                                        height={getChartHeight(
                                            chartConfig.type,
                                            data.rows.length
                                        )}
                                    >
                                        <ResponsiveContainer
                                            width="100%"
                                            height="100%"
                                        >
                                            {chartConfig.type === 'pie' ? (
                                                <PieChart>
                                                    <Pie
                                                        data={pieData}
                                                        dataKey={chartYKeys[0]}
                                                        nameKey={chartXKey}
                                                        cx="50%"
                                                        cy="50%"
                                                        outerRadius="80%"
                                                        innerRadius="45%"
                                                        paddingAngle={2}
                                                        label={({
                                                            name,
                                                            percent
                                                        }) =>
                                                            percent > 0.04
                                                                ? `${name} (${(
                                                                      percent *
                                                                      100
                                                                  ).toFixed(
                                                                      0
                                                                  )}%)`
                                                                : ''
                                                        }
                                                        labelLine={false}
                                                    >
                                                        {pieData.map(
                                                            (_, idx) => (
                                                                <Cell
                                                                    key={idx}
                                                                    fill={
                                                                        CHART_COLORS[
                                                                            idx %
                                                                                CHART_COLORS.length
                                                                        ]
                                                                    }
                                                                />
                                                            )
                                                        )}
                                                    </Pie>
                                                    <Tooltip
                                                        formatter={
                                                            formatTooltipValue
                                                        }
                                                    />
                                                    <Legend
                                                        layout="horizontal"
                                                        verticalAlign="bottom"
                                                        align="center"
                                                        wrapperStyle={{
                                                            fontSize: 12,
                                                            paddingTop: 12
                                                        }}
                                                    />
                                                </PieChart>
                                            ) : chartConfig.type === 'line' ? (
                                                <LineChart data={data.rows}>
                                                    <CartesianGrid strokeDasharray="3 3" />
                                                    <XAxis
                                                        dataKey={chartXKey}
                                                        angle={xAxisAngle}
                                                        textAnchor={
                                                            xAxisAngle
                                                                ? 'end'
                                                                : 'middle'
                                                        }
                                                        height={
                                                            xAxisAngle ? 80 : 30
                                                        }
                                                        tick={{ fontSize: 12 }}
                                                    />
                                                    <YAxis
                                                        tick={{ fontSize: 12 }}
                                                    />
                                                    <Tooltip
                                                        formatter={
                                                            formatTooltipValue
                                                        }
                                                    />
                                                    {chartYKeys.length > 1 && (
                                                        <Legend
                                                            wrapperStyle={{
                                                                fontSize: 12,
                                                                paddingTop: 8
                                                            }}
                                                        />
                                                    )}
                                                    {chartYKeys.map(
                                                        (dk, idx) => (
                                                            <Line
                                                                key={dk}
                                                                type="monotone"
                                                                dataKey={dk}
                                                                stroke={
                                                                    CHART_COLORS[
                                                                        idx %
                                                                            CHART_COLORS.length
                                                                    ]
                                                                }
                                                                strokeWidth={2}
                                                                dot={{ r: 3 }}
                                                            />
                                                        )
                                                    )}
                                                </LineChart>
                                            ) : (
                                                <BarChart data={data.rows}>
                                                    <CartesianGrid strokeDasharray="3 3" />
                                                    <XAxis
                                                        dataKey={chartXKey}
                                                        angle={xAxisAngle}
                                                        textAnchor={
                                                            xAxisAngle
                                                                ? 'end'
                                                                : 'middle'
                                                        }
                                                        height={
                                                            xAxisAngle ? 80 : 30
                                                        }
                                                        tick={{ fontSize: 12 }}
                                                    />
                                                    <YAxis
                                                        tick={{ fontSize: 12 }}
                                                    />
                                                    <Tooltip
                                                        formatter={
                                                            formatTooltipValue
                                                        }
                                                    />
                                                    {chartYKeys.length > 1 && (
                                                        <Legend
                                                            wrapperStyle={{
                                                                fontSize: 12,
                                                                paddingTop: 8
                                                            }}
                                                        />
                                                    )}
                                                    {chartYKeys.map(
                                                        (dk, idx) => (
                                                            <Bar
                                                                key={dk}
                                                                dataKey={dk}
                                                                fill={
                                                                    CHART_COLORS[
                                                                        idx %
                                                                            CHART_COLORS.length
                                                                    ]
                                                                }
                                                            />
                                                        )
                                                    )}
                                                </BarChart>
                                            )}
                                        </ResponsiveContainer>
                                    </Box>
                                </Box>
                            )}
                    </>
                )}

                {data && !hasRows && !isFetching && (
                    <Box px={2} pb={3}>
                        <SMRegular color="text.secondary">
                            No data found for the selected filters.
                        </SMRegular>
                    </Box>
                )}
            </TableCard>
        </Box>
    );
};
